// Coupons: the catalogue of offers, and who has been given one.
//
// A coupon here is only a definition. Nothing happens to anyone's bill until it
// is granted to a person — from this page or from their profile — which creates
// a credit against their number. The credit is applied at checkout and only
// spent once the payment is actually captured, so an abandoned basket gives it
// back.
import React, { useCallback, useEffect, useState } from 'react';
import {
  Box, Typography, TextField, Button, MenuItem, Alert, Divider, Chip,
  Table, TableBody, TableCell, TableHead, TableRow, Switch, Tooltip,
} from '@mui/material';
import { supabase } from './supabaseClient';

const usd = (cents) =>
  cents == null ? '—' : `$${(Number(cents) / 100).toFixed(2)}`;

export default function CouponsPage() {
  const [coupons, setCoupons] = useState([]);
  const [credits, setCredits] = useState([]);
  const [message, setMessage] = useState(null);
  const [saving, setSaving] = useState(false);

  const [code, setCode] = useState('');
  const [description, setDescription] = useState('');
  const [kind, setKind] = useState('percent');
  const [value, setValue] = useState('');
  const [maxRedemptions, setMaxRedemptions] = useState('');
  const [expiresAt, setExpiresAt] = useState('');

  const [grantPhone, setGrantPhone] = useState('');
  const [grantCode, setGrantCode] = useState('');
  const [grantNote, setGrantNote] = useState('');

  const load = useCallback(async () => {
    const [{ data: c }, { data: g }] = await Promise.all([
      supabase.from('coupons').select('*').order('created_at', { ascending: false }),
      supabase.from('profile_credits').select('*').order('granted_at', { ascending: false }).limit(100),
    ]);
    setCoupons(c || []);
    setCredits(g || []);
  }, []);

  useEffect(() => { load(); }, [load]);

  const createCoupon = async () => {
    const numeric = Number(value);
    if (!code.trim() || !Number.isFinite(numeric) || numeric <= 0) {
      setMessage({ severity: 'warning', text: 'A coupon needs a code and a value.' });
      return;
    }
    setSaving(true);
    setMessage(null);
    const row = {
      code: code.trim().toUpperCase(),
      description: description.trim() || null,
      kind,
      value_percent: kind === 'percent' ? Math.round(numeric) : null,
      amount_cents: kind === 'amount' ? Math.round(numeric * 100) : null,
      max_redemptions: maxRedemptions ? Math.round(Number(maxRedemptions)) : null,
      // End of the chosen day, in the admin's own timezone.
      //
      // A type="date" field gives "2026-10-18", and new Date() reads that as
      // midnight UTC — which is the evening *before* across the Americas. A
      // coupon set to expire on the 18th was dying at 8pm on the 17th, a day
      // early, and the only sign was a customer being told their code had
      // expired. Appending the time makes it parse as local.
      expires_at: expiresAt ? new Date(`${expiresAt}T23:59:59`).toISOString() : null,
    };
    const { error } = await supabase.from('coupons').insert(row);
    if (error) {
      // The unique index is on upper(code), so a duplicate in any casing lands here.
      setMessage({ severity: 'error', text: error.message });
    } else {
      setCode(''); setDescription(''); setValue(''); setMaxRedemptions(''); setExpiresAt('');
      setMessage({ severity: 'success', text: 'Coupon created. Grant it to someone to make it count.' });
      await load();
    }
    setSaving(false);
  };

  const toggleActive = async (coupon) => {
    const { error } = await supabase
      .from('coupons').update({ is_active: !coupon.is_active }).eq('id', coupon.id);
    if (error) setMessage({ severity: 'error', text: error.message });
    await load();
  };

  const grant = async () => {
    const coupon = coupons.find((c) => c.code.toUpperCase() === grantCode.trim().toUpperCase());
    if (!grantPhone.trim() || !coupon || !grantNote.trim()) {
      setMessage({ severity: 'warning', text: 'Pick a coupon, a phone number, and say why.' });
      return;
    }
    setSaving(true);
    setMessage(null);
    const { error } = await supabase.rpc('admin_grant_profile_credit', {
      p_phone_number: grantPhone.trim(),
      p_kind: coupon.kind,
      p_value: coupon.kind === 'percent' ? coupon.value_percent : coupon.amount_cents,
      p_note: grantNote.trim(),
      p_coupon_code: coupon.code,
      p_expires_at: null,
    });
    if (error) {
      setMessage({ severity: 'error', text: error.message });
    } else {
      setGrantPhone(''); setGrantNote('');
      setMessage({ severity: 'success', text: 'Given. It comes off their next booking.' });
      await load();
    }
    setSaving(false);
  };

  const describe = (row) =>
    row.kind === 'percent' ? `${row.value_percent}% off` : `${usd(row.amount_cents)} off`;

  const creditState = (c) => {
    if (c.consumed_at) return <Chip size="small" color="default" label="Used" />;
    if (c.reserved_booking_id) return <Chip size="small" color="warning" label="Held for a booking" />;
    if (c.expires_at && new Date(c.expires_at) < new Date()) return <Chip size="small" label="Expired" />;
    return <Chip size="small" color="success" label="Waiting" />;
  };

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h6" sx={{ mb: 0.5 }}>Coupons</Typography>
      <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
        A coupon is a definition. Nothing changes for anyone until you give it to someone —
        here or from their profile. It then comes off their next booking automatically, and is
        only spent once that payment goes through.
      </Typography>

      {message && <Alert severity={message.severity} sx={{ mb: 2 }}>{message.text}</Alert>}

      <Typography variant="subtitle2" sx={{ mb: 1 }}>New coupon</Typography>
      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1 }}>
        <TextField size="small" label="Code" value={code} onChange={(e) => setCode(e.target.value)} sx={{ width: 160 }} />
        <TextField size="small" select label="Type" value={kind} onChange={(e) => setKind(e.target.value)} sx={{ width: 140 }}>
          <MenuItem value="percent">Percentage</MenuItem>
          <MenuItem value="amount">Fixed amount</MenuItem>
        </TextField>
        <TextField
          size="small"
          label={kind === 'percent' ? 'Percent off' : 'Amount off ($)'}
          value={value}
          onChange={(e) => setValue(e.target.value)}
          sx={{ width: 150 }}
        />
        <TextField size="small" label="Max uses (optional)" value={maxRedemptions} onChange={(e) => setMaxRedemptions(e.target.value)} sx={{ width: 170 }} />
        <TextField size="small" type="date" label="Valid through (optional)" InputLabelProps={{ shrink: true }} value={expiresAt} onChange={(e) => setExpiresAt(e.target.value)} sx={{ width: 180 }} />
      </Box>
      <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
        <TextField size="small" label="Description" value={description} onChange={(e) => setDescription(e.target.value)} fullWidth />
        <Button variant="contained" onClick={createCoupon} disabled={saving}>Create</Button>
      </Box>

      <Table size="small" sx={{ mb: 3 }}>
        <TableHead>
          <TableRow>
            <TableCell>Code</TableCell>
            <TableCell>Offer</TableCell>
            <TableCell>Used</TableCell>
            <TableCell>Expires</TableCell>
            <TableCell>Active</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {coupons.length === 0 && (
            <TableRow><TableCell colSpan={5}>No coupons yet.</TableCell></TableRow>
          )}
          {coupons.map((c) => (
            <TableRow key={c.id}>
              <TableCell>{c.code}</TableCell>
              <TableCell>
                {describe(c)}
                {c.description ? ` — ${c.description}` : ''}
              </TableCell>
              <TableCell>
                {c.times_redeemed}{c.max_redemptions ? ` of ${c.max_redemptions}` : ''}
              </TableCell>
              <TableCell>{c.expires_at ? new Date(c.expires_at).toLocaleDateString() : '—'}</TableCell>
              <TableCell>
                <Tooltip title={c.is_active ? 'Switch off' : 'Switch on'}>
                  <Switch size="small" checked={!!c.is_active} onChange={() => toggleActive(c)} />
                </Tooltip>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      <Divider sx={{ my: 2 }} />

      <Typography variant="subtitle2" sx={{ mb: 1 }}>Give a coupon to someone</Typography>
      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 3 }}>
        <TextField size="small" label="Phone number" value={grantPhone} onChange={(e) => setGrantPhone(e.target.value)} sx={{ width: 190 }} />
        <TextField size="small" select label="Coupon" value={grantCode} onChange={(e) => setGrantCode(e.target.value)} sx={{ width: 200 }}>
          {coupons.filter((c) => c.is_active).map((c) => (
            <MenuItem key={c.id} value={c.code}>{c.code} — {describe(c)}</MenuItem>
          ))}
        </TextField>
        <TextField size="small" label="Reason" value={grantNote} onChange={(e) => setGrantNote(e.target.value)} sx={{ minWidth: 240, flex: 1 }} />
        <Button variant="outlined" onClick={grant} disabled={saving}>Give</Button>
      </Box>

      <Typography variant="subtitle2" sx={{ mb: 1 }}>Recently given</Typography>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Who</TableCell>
            <TableCell>What</TableCell>
            <TableCell>Reason</TableCell>
            <TableCell>Given by</TableCell>
            <TableCell>State</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {credits.length === 0 && (
            <TableRow><TableCell colSpan={5}>Nothing given yet.</TableCell></TableRow>
          )}
          {credits.map((c) => (
            <TableRow key={c.id}>
              <TableCell>{c.phone_number}</TableCell>
              <TableCell>{describe(c)}</TableCell>
              <TableCell>{c.note}</TableCell>
              <TableCell>{c.granted_by || '—'}</TableCell>
              <TableCell>{creditState(c)}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Box>
  );
}
